import axios from 'axios';

const FDA_BASE = 'https://api.fda.gov';
const TRIALS_BASE = 'https://clinicaltrials.gov/api/v2';

// Optional openFDA API key — raises the rate limit from 240 req/min (shared
// per IP) to 240 req/min per key with a much higher daily cap. Set
// REACT_APP_OPENFDA_KEY in .env (local) or the Render environment (deploy).
const FDA_KEY = process.env.REACT_APP_OPENFDA_KEY;

function withKey(url) {
  if (!FDA_KEY) return url;
  return `${url}${url.includes('?') ? '&' : '?'}api_key=${FDA_KEY}`;
}

const SPELL_CORRECTIONS = {
  'semaglitide': 'semaglutide',
  'semagludite': 'semaglutide',
  'accutane': 'isotretinoin',
  'rogaine': 'minoxidil',
  'propecia': 'finasteride',
  'glucophage': 'metformin',
  'advil': 'ibuprofen',
  'tylenol': 'acetaminophen',
  'zoloft': 'sertraline',
  'prozac': 'fluoxetine',
  'adderall': 'amphetamine',
  'ritalin': 'methylphenidate',
};

export function getSuggestion(query) {
  const lower = query.toLowerCase().trim();
  if (SPELL_CORRECTIONS[lower]) return SPELL_CORRECTIONS[lower];

  const allKnownTerms = [
    'semaglutide', 'isotretinoin', 'minoxidil', 'finasteride',
    'metformin', 'ibuprofen', 'acetaminophen', 'sertraline',
    'fluoxetine', 'amphetamine', 'methylphenidate',
    ...Object.keys(SPELL_CORRECTIONS)
  ];

  const match = allKnownTerms.find(term => {
    if (Math.abs(term.length - lower.length) > 4) return false;
    return levenshtein(lower, term) <= 3;
  });

  return match ? SPELL_CORRECTIONS[match] || match : null;
}

function levenshtein(a, b) {
  const dp = Array.from({ length: a.length + 1 }, (_, i) =>
    Array.from({ length: b.length + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
  );
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }
  return dp[a.length][b.length];
}


export async function searchDrug(drugName) {
  // Step 1: resolve identity — get FDA label to find generic name
  const drugInfo = await getDrugInfo(drugName);

  // Step 2: fetch data using exactly what was searched — brand and generic
  // intentionally return different results (ozempic ≠ semaglutide)
  const [adverseEvents, recalls, trials, brandNames] = await Promise.all([
    getAdverseEvents(drugName),
    getRecalls(drugName),
    getClinicalTrials(drugName),
    drugInfo?.genericName && drugInfo.genericName !== 'Unknown'
      ? getBrandNames(drugInfo.genericName)
      : Promise.resolve([]),
  ]);

  const hasData = drugInfo || adverseEvents.length > 0 || trials.length > 0;

  let didYouMean = null;
  if (!drugInfo && trials.length === 0) {
    const hardcoded = getSuggestion(drugName);
    if (hardcoded && hardcoded.toLowerCase() !== drugName.toLowerCase()) {
      didYouMean = hardcoded;
    } else {
      didYouMean = await getFDASuggestion(drugName);
    }
  }

  return {
    name: drugName,
    drugInfo,
    brandNames,                           // all known brand names for this molecule
    adverseEvents,
    recalls,
    trials,
    conditionMap: buildConditionMap(trials, drugName),
    evidenceTier: calculateEvidenceTier(trials, drugInfo),
    didYouMean,
    hasData,
  };
}

async function getFDASuggestion(drugName) {
  const words = drugName.trim().split(/\s+/).filter(w => w.length >= 4);
  const candidates = [drugName, ...words];

  for (const term of candidates) {
    try {
      const res = await axios.get(withKey(
        `${FDA_BASE}/drug/label.json?search=openfda.brand_name:${encodeURIComponent(term)}*&limit=1`
      ));
      const result = res.data.results?.[0];
      const suggestion = result?.openfda?.brand_name?.[0];
      if (suggestion && suggestion.toLowerCase() !== drugName.toLowerCase()) {
        return suggestion;
      }
    } catch {
      // try next candidate
    }
  }
  return null;
}

async function getDrugInfo(drugName) {
  try {
    const encoded = encodeURIComponent(drugName);
    // Try brand name first, fall back to generic name
    let result = null;
    for (const field of ['openfda.brand_name', 'openfda.generic_name']) {
      try {
        const res = await axios.get(withKey(
          `${FDA_BASE}/drug/label.json?search=${field}:"${encoded}"&limit=1`
        ));
        if (res.data.results?.[0]) { result = res.data.results[0]; break; }
      } catch { /* try next */ }
    }
    if (!result) return null;

    let purpose = result.purpose?.[0] || result.indications_and_usage?.[0] || 'See FDA label';
    purpose = purpose
      .replace(/^purpose\s*/i, '')
      .replace(/^\d+\s+indications and usage\s*/i, '')
      .trim();

    return {
      brandName: result.openfda?.brand_name?.[0] || drugName,
      genericName: result.openfda?.generic_name?.[0] || 'Unknown',
      manufacturer: result.openfda?.manufacturer_name?.[0] || 'Unknown',
      purpose,
      warnings: result.warnings?.[0] || null,
      boxedWarning: result.boxed_warning?.[0] || null,
      contraindications: result.contraindications?.[0] || null,
      drugInteractions: result.drug_interactions?.[0] || null,
      approvalDate: result.openfda?.application_number?.[0] || 'Unknown',
    };
  } catch {
    return null;
  }
}

async function getBrandNames(genericName) {
  try {
    const res = await axios.get(withKey(
      `${FDA_BASE}/drug/label.json?search=openfda.generic_name:"${genericName}"&limit=10`
    ));
    const brands = new Set();
    res.data.results.forEach(r => {
      (r.openfda?.brand_name || []).forEach(b => brands.add(b));
    });
    return Array.from(brands).slice(0, 8);
  } catch {
    return [];
  }
}

async function getAdverseEvents(drugName) {
  try {
    const res = await axios.get(withKey(
      `${FDA_BASE}/drug/event.json?search=patient.drug.medicinalproduct:"${drugName}"&count=patient.reaction.reactionmeddrapt.exact&limit=5`
    ));
    return res.data.results.map(r => ({ reaction: r.term, count: r.count }));
  } catch {
    return [];
  }
}

export async function getRecalls(drugName) {
  try {
    const res = await axios.get(withKey(
      `${FDA_BASE}/drug/enforcement.json?search=product_description:"${drugName}"&limit=5`
    ));
    const seen = new Set();
    return res.data.results
      .map(r => ({
        reason: r.reason_for_recall,
        date: r.recall_initiation_date,
        status: r.status,
      }))
      .filter(r => {
        const key = `${r.reason}|${r.date}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, 3);
  } catch {
    return [];
  }
}

// Pulls actual enrolled-participant sex counts from a trial's posted
// results (not eligibility criteria). Only completed trials that have
// formally reported results carry this — most trials won't have it.
function extractSexEnrollment(resultsSection) {
  const measures = resultsSection?.baselineCharacteristicsModule?.measures || [];
  const sexMeasure = measures.find(m => /^sex/i.test(m.title || '') || /^gender/i.test(m.title || ''));
  if (!sexMeasure) return null;

  let female = 0;
  let male = 0;
  (sexMeasure.classes || []).forEach(cls => {
    (cls.categories || []).forEach(cat => {
      const title = (cat.title || '').trim().toLowerCase();
      const sum = (cat.measurements || []).reduce((acc, m) => acc + (parseInt(m.value, 10) || 0), 0);
      if (title === 'female') female += sum;
      else if (title === 'male') male += sum;
    });
  });

  return female + male > 0 ? { female, male } : null;
}

async function getClinicalTrials(drugName) {
  try {
    const fields = [
      'protocolSection.identificationModule.briefTitle',
      'protocolSection.designModule.phases',
      'protocolSection.designModule.enrollmentInfo',
      'protocolSection.statusModule.overallStatus',
      'protocolSection.statusModule.startDateStruct',
      'protocolSection.statusModule.completionDateStruct',
      'protocolSection.conditionsModule.conditions',
      'resultsSection.baselineCharacteristicsModule',
    ].join(',');
    const res = await axios.get(
      `${TRIALS_BASE}/studies?query.term=${encodeURIComponent(drugName)}&pageSize=50&fields=${fields}`
    );
    const studies = res.data.studies || [];
    return studies.map(s => {
      const proto = s.protocolSection;
      return {
        title: proto?.identificationModule?.briefTitle || 'Untitled',
        phase: proto?.designModule?.phases?.[0] || 'Unknown',
        enrollment: proto?.designModule?.enrollmentInfo?.count || 0,
        status: proto?.statusModule?.overallStatus || 'Unknown',
        startDate: proto?.statusModule?.startDateStruct?.date || 'Unknown',
        completionDate: proto?.statusModule?.completionDateStruct?.date || 'Unknown',
        conditions: proto?.conditionsModule?.conditions || [],
        sexEnrollment: extractSexEnrollment(s.resultsSection),
      };
    });
  } catch {
    return [];
  }
}

// Trial "conditions" that aren't real conditions: healthy-volunteer studies
// and entries that just restate the drug being tested
function isNoiseCondition(condition, drugName) {
  const c = condition.toLowerCase();
  if (c === 'healthy' || c.includes('healthy volunteer') || c.includes('healthy subject') || c.includes('healthy participant')) return true;
  if (drugName && c.includes(drugName.toLowerCase())) return true;
  return false;
}

// Common abbreviation/synonym pairs so e.g. "Hypertension" and "High Blood
// Pressure" merge into one line item instead of listing separately. Applied
// as whole-phrase substitutions before the condition is tokenized.
const CONDITION_SYNONYMS = [
  [/\bhypertension\b/g, 'high blood pressure'],
  [/\bmyocardial infarction\b/g, 'heart attack'],
  [/\bhypercholesterolemia\b/g, 'high cholesterol'],
  [/\bt2dm\b/g, 'type 2 diabetes'],
  [/\bt1dm\b/g, 'type 1 diabetes'],
  [/\bnafld\b/g, 'nonalcoholic fatty liver disease'],
  [/\bnash\b/g, 'nonalcoholic steatohepatitis'],
  [/\bcopd\b/g, 'chronic obstructive pulmonary disease'],
  [/\bgerd\b/g, 'gastroesophageal reflux disease'],
  [/\bibs\b/g, 'irritable bowel syndrome'],
  [/\brheumatoid arthritis\b/g, 'rheumatoid arthritis'],
  [/\bosa\b/g, 'obstructive sleep apnea'],
  [/\bckd\b/g, 'chronic kidney disease'],
];

// Words that don't distinguish one condition from another for merge purposes
// (unlike "type" or a number, which do — "Type 2" vs "Type 1" must stay
// distinct). Dropped only when building the merge key, not from the label
// people actually see.
const CONDITION_MERGE_STOPWORDS = new Set(['mellitus', 'disease', 'disorder', 'syndrome', 'condition']);

// Case/punctuation/word-order-insensitive key so "Type 2 Diabetes",
// "Diabetes Mellitus, Type 2", and "Type 2 Diabetes Mellitus" all collapse
// to the same entry instead of listing as three separate conditions.
function conditionMergeKey(condition) {
  let text = condition.toLowerCase();
  CONDITION_SYNONYMS.forEach(([pattern, replacement]) => { text = text.replace(pattern, replacement); });
  const tokens = text
    .replace(/[(),]/g, ' ')
    .replace(/([a-z])(\d)/g, '$1 $2')  // "Type2" → "Type 2" so it tokenizes the same as "Type 2"
    .replace(/(\d)([a-z])/g, '$1 $2')
    .split(/\s+/)
    .filter(t => t && !CONDITION_MERGE_STOPWORDS.has(t))
    .sort();
  return tokens.join(' ');
}

function buildConditionMap(trials, drugName) {
  const map = {};
  trials.forEach(trial => {
    const phase = trial.phase;
    trial.conditions.forEach(condition => {
      if (isNoiseCondition(condition, drugName)) return;
      const normalized = normalizeCondition(condition);
      const target = conditionMergeKey(normalized);
      if (!target) return;
      if (!map[target]) map[target] = { labels: [], count: 0, phases: new Set() };
      map[target].labels.push(normalized);
      map[target].count += 1;
      if (phase && phase !== 'Unknown') map[target].phases.add(phase);
    });
  });

  return Object.values(map)
    // Prefer the cleanest-looking surface form: fewer "smashed together"
    // letter/digit boundaries (e.g. "Type2diabetes" from messy source data),
    // then shortest among the well-formatted ones — e.g. "Type 2 Diabetes"
    // over "Diabetes Mellitus, Type 2"
    .map(({ labels, count, phases }) => ({
      condition: labels.reduce((best, l) => {
        const bestScore = (best.match(/[a-zA-Z]\d|\d[a-zA-Z]/g) || []).length;
        const lScore = (l.match(/[a-zA-Z]\d|\d[a-zA-Z]/g) || []).length;
        if (lScore < bestScore) return l;
        if (lScore === bestScore && l.length < best.length) return l;
        return best;
      }, labels[0]),
      count,
      phases: Array.from(phases),
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);
}

function normalizeCondition(condition) {
  return condition.toLowerCase().replace(/\s+/g, ' ').trim().replace(/\b\w/g, c => c.toUpperCase());
}

function calculateEvidenceTier(trials, drugInfo) {
  if (!drugInfo && trials.length === 0) {
    return { label: 'No FDA Data', color: '#999', description: 'No FDA approval or clinical trial data found for this product.' };
  }

  const phase3or4 = trials.filter(t => t.phase === 'PHASE3' || t.phase === 'PHASE4').length;
  const largeTrials = trials.filter(t => t.enrollment > 500).length;
  const totalEnrollment = trials.reduce((sum, t) => sum + t.enrollment, 0);

  // Well-Studied requires real scale: multiple late-phase trials AND 2,000+
  // total participants. A single 500-person trial is not enough.
  if (phase3or4 >= 3 && totalEnrollment >= 2000) {
    return { label: 'Well-Studied', color: '#2e7d32', description: 'Multiple large Phase 3 or 4 trials exist. Long-term evidence is available.' };
  } else if (phase3or4 >= 1 && largeTrials >= 1) {
    return { label: 'Moderate Evidence', color: '#f57c00', description: 'Some larger trials exist but long-term or large-scale data may be limited.' };
  } else if (trials.length >= 3 || phase3or4 >= 1) {
    return { label: 'Moderate Evidence', color: '#f57c00', description: 'Some larger trials exist but long-term or large-scale data may be limited.' };
  } else if (trials.length > 0) {
    return { label: 'Thin Evidence', color: '#c62828', description: 'Only early-phase or small trials found. Long-term safety is not well established.' };
  } else if (drugInfo) {
    return { label: 'Thin Evidence', color: '#c62828', description: 'FDA-approved drug, but no clinical trial data was found in ClinicalTrials.gov. Long-term evidence may be limited.' };
  } else {
    return { label: 'No FDA Data', color: '#999', description: 'No FDA approval or clinical trial data found. This may be a supplement or unapproved product.' };
  }
}
